/**
 * @author NikolaV
 */
import { action, computed, makeObservable, observable } from 'mobx';
import { DateUtils } from '@shared/utils/DateUtils';
import { store } from '../store/rootStore';
import { IFilterItem } from '@shared/ui-react/CheckboxListDropdown';
import { GeofenceType } from '@shared/trucks/GeofenceFeatureFactory';


export class FilterStore {
    @observable fromTimestamp = DateUtils.presets[2].from;
    @observable toTimestamp = DateUtils.presets[2].to;
    @observable datePresetIndex = null;

    @observable distance = {
        // in meters, and ui will work with km
        from: null,
        to: null,
    };

    @observable vehicles = new Map<number, IFilterItem>();
    @observable materials = new Map<number, IFilterItem>();
    @observable tasks = new Map<number, IFilterItem>();
    @observable loadAreas = new Map<number, IFilterItem>();
    @observable unloadAreas = new Map<number, IFilterItem>();
    @observable gpsLoadAreas = new Map<number, IFilterItem>();
    @observable gpsUnloadAreas = new Map<number, IFilterItem>();
    @observable includeEmptyTrips: null | 'yes' | 'onlyEmpty' = null;

    readonly allFilters: Array<Map<number, IFilterItem>> = [];
    readonly allFiltersNames = ['vehicles', 'materials', 'tasks', 'loadAreas', 'unloadAreas', 'gpsLoadAreas', 'gpsUnloadAreas'];

    constructor() {
        makeObservable(this);

        this.allFilters = [this.vehicles, this.materials, this.tasks, this.loadAreas, this.unloadAreas, this.gpsLoadAreas, this.gpsUnloadAreas];
    }

    @action
    setFromStoresData() {
        store.vehicles.items.forEach(v => {
            this.vehicles.set(v.id, {
                title: v.name,
                value: v.id,
                active: false,
            });
        });

        store.tasks.items.forEach(t => {
            this.tasks.set(t.id, {
                title: t.name,
                value: t.id,
                active: true,
            });
        });

        this.tasks.set(-1, {
            title: kuura.getMessage('trucks.filter.no_task'),
            value: -1,
            active: true,
        });

        store.geofences.items.forEach(area => {
            if (area.type === GeofenceType.LOAD || area.type === GeofenceType.HYBRID) {
                this.loadAreas.set(area.id, {
                    title: area.title,
                    value: area.id,
                    active: true,
                });
                this.gpsLoadAreas.set(area.id, {
                    title: area.title,
                    value: area.id,
                    active: true,
                });
            }
            if (area.type === GeofenceType.UNLOAD || area.type === GeofenceType.HYBRID) {
                this.unloadAreas.set(area.id, {
                    title: area.title,
                    value: area.id,
                    active: true,
                });
                this.gpsUnloadAreas.set(area.id, {
                    title: area.title,
                    value: area.id,
                    active: true,
                });
            }
        });

        this.loadAreas.set(-1, {
            title: kuura.getMessage('trucks.filter.no_area'),
            value: -1,
            active: true,
        });

        this.unloadAreas.set(-1, {
            title: kuura.getMessage('trucks.filter.no_area'),
            value: -1,
            active: true,
        });

        this.gpsLoadAreas.set(-1, {
            title: kuura.getMessage('trucks.filter.no_area'),
            value: -1,
            active: true,
        });

        this.gpsUnloadAreas.set(-1, {
            title: kuura.getMessage('trucks.filter.no_area'),
            value: -1,
            active: true,
        });

        store.materials.items.forEach(mat => {
            this.materials.set(mat.id, {
                title: mat.material,
                value: mat.id,
                active: true,
            });
        });

        this.materials.set(-1, {
            title: kuura.getMessage('trucks.filter.no_material'),
            value: -1,
            active: true,
        });
    }

    /*@action
    setFromStatsData(stats: ITripStat[]) {
        stats.forEach(s => {
            if (!this.vehicles.has(String(s.vehicleId))) {
                this.vehicles.set(String(s.vehicleId), {
                    title: s.vehicleTitle,
                    value: s.vehicleId,
                    active: true,
                });
            }

            if (s.taskId && !this.tasks.has(String(s.taskId))) {
                this.tasks.set(String(s.taskId), {
                    title: s.taskTitle,
                    value: s.taskId,
                    active: true,
                });
            }

            if (s.loadAreaId && !this.loadAreas.has(String(s.loadAreaId))) {
                this.loadAreas.set(String(s.loadAreaId), {
                    title: s.loadAreaTitle,
                    value: s.loadAreaId,
                    active: true,
                });
            }

            if (s.unloadAreaId && !this.unloadAreas.has(String(s.unloadAreaId))) {
                this.unloadAreas.set(String(s.unloadAreaId), {
                    title: s.unloadAreaTitle,
                    value: s.unloadAreaId,
                    active: true,
                });
            }
        });
    }*/

    @action.bound
    setFromUrlString(params: any) {
        this.allFilters.forEach((filterMap, i) => {
            const values = params[this.allFiltersNames[i]];
            if (!values || values.length === 0) {
                this.selectAllNone(true, [filterMap]);
            } else {
                this.selectAllNone(false, [filterMap]);
                values.split(',').forEach(part => {
                    if (filterMap.has(part)) {
                        filterMap.get(part).active = true;
                    }
                });
            }
        });

        if (params.fromTimestamp) {
            const m = moment(parseInt(params.fromTimestamp, 10));
            if (m.isValid()) {
                this.fromTimestamp = +m;
            }
        } else {
            this.fromTimestamp = null;
        }

        if (params.toTimestamp) {
            const m = moment(parseInt(params.toTimestamp, 10));
            if (m.isValid()) {
                this.toTimestamp = +m;
            }
        } else {
            this.toTimestamp = null;
        }

        if (params.hasOwnProperty('distanceFrom')) {
            this.distance.from = params.distanceFrom;
        }

        if (params.hasOwnProperty('distanceTo')) {
            this.distance.to = params.distanceTo;
        }
        if (params.hasOwnProperty('includeEmptyTrips')) {
            this.includeEmptyTrips = params.includeEmptyTrips;
        }
    }

    @computed
    get urlString() {
        let str = '';

        if (this.fromTimestamp) {
            str += '&fromTimestamp=' + moment(this.fromTimestamp).startOf('day').valueOf();
        }

        if (this.toTimestamp) {
            str += '&toTimestamp=' + moment(this.toTimestamp).endOf('day').valueOf();
        }

        if (this.distance.from != null) {
            str += '&distanceFrom=' + this.distance.from;
        }

        if (this.distance.to != null) {
            str += '&distanceTo=' + this.distance.to;
        }
        if (this.includeEmptyTrips) {
            str += '&includeEmptyTrips=' + this.includeEmptyTrips;
        }

        this.allFilters.forEach((filterMap, i) => {
            const entries = Array.from(filterMap.entries());
            const enabled = entries.filter(pair => !!pair[1].active);

            if (enabled.length !== entries.length) {
                const values = enabled.map(pair => pair[1].value); // take key name
                str += '&' + this.allFiltersNames[i] + '=' + values.join(',');
            }
        });
        return str;
    }

    @action.bound
    setDates(from: number, to: number, datePresetIndex: number) {
        this.fromTimestamp = from;
        this.toTimestamp = to;
        this.datePresetIndex = datePresetIndex;
    }

    @action.bound
    setDistance(from: number, to: number) {
        if (from != null && $.isNumeric(from)) {
            this.distance.from = Math.max(0, from);
        } else {
            this.distance.from = null;
        }

        if (to != null && $.isNumeric(to)) {
            this.distance.to = Math.max(0, to);
        } else {
            this.distance.to = null;
        }
    }

    @action.bound
    selectAll() {
        this.selectAllNone(true);
    }

    @action.bound
    selectAllWithAllDates() {
        this.selectAllNone(true);
        this.fromTimestamp = null;
        this.setDistance(null, null);
    }

    @action.bound
    selectNone() {
        this.selectAllNone(false);
    }

    @action
    selectAllNone = (state: boolean, filters = this.allFilters) => {
        for (const filterMap of filters) {
            filterMap.forEach((val, key) => (filterMap.get(key).active = state));
        }

        this.distance.from = null;
        this.distance.to = null;
    };

    @action.bound
    deselectVehicles() {
        this.selectAllNone(false, [this.vehicles]);
    }

    @action.bound
    focusVehicle(id: number, selectAllOthers = true) {
        if (selectAllOthers) {
            this.selectAll();
        }
        this.selectAllNone(false, [this.vehicles]);

        if (this.vehicles.has(id)) {
            this.vehicles.get(id).active = true;
        } else {
            console.log('vehicle not found');
        }
    }

    isVehicleSelected(id: number) {
        if (!this.vehicles) return false;
        return this.vehicles.get(id)?.active;
    }

    @action.bound
    focusArea(id: number, selectAllOthers = true, resetOthers = false) {
        if (selectAllOthers) {
            this.selectAll();
        }
        if (resetOthers) {
            this.fromTimestamp = null;
            this.toTimestamp = null;
            this.selectAll();
        }
        if (this.loadAreas.has(id)) {
            this.selectAllNone(false, [this.loadAreas]);
            this.loadAreas.get(id).active = true;
        }
        if (this.unloadAreas.has(id)) {
            this.selectAllNone(false, [this.unloadAreas]);
            this.unloadAreas.get(id).active = true;
        }
        // we decided not to set gps filter when opening with quick stats button
        //if (this.gpsLoadAreas.has(id)) {
        //    this.selectAllNone(false, [this.gpsLoadAreas]);
        //    this.gpsLoadAreas.get(id).active = true;
        //}
        //if (this.gpsUnloadAreas.has(id)) {
        //    this.selectAllNone(false, [this.gpsUnloadAreas]);
        //    this.gpsUnloadAreas.get(id).active = true;
        //}
    }

    getActiveIdsForFilter(filterName: string): any[] {
        const entries = Array.from(this[filterName].entries() || []);
        const enabled = entries.filter(pair => !!pair[1].active);

        return enabled.map(pair => pair[1].value);
    }

    isAllIdsActiveForFilter(filterName: string): boolean {
        const entries = Array.from(this[filterName].entries() || []);
        const enabled = entries.filter(e => e[1].active).length;

        return enabled === entries.length;
    }

    toApiFilter = () => {
        const materialIds = this.getActiveIdsForFilter('materials');
        const vehicleIds = this.getActiveIdsForFilter('vehicles');
        const taskIds = this.getActiveIdsForFilter('tasks');
        const loadAreasIds = this.getActiveIdsForFilter('loadAreas');
        const unloadAreasIds = this.getActiveIdsForFilter('unloadAreas');
        const gpsLoadAreasIds = this.getActiveIdsForFilter('gpsLoadAreas');
        const gpsUnloadAreasIds = this.getActiveIdsForFilter('gpsUnloadAreas');

        const opts: any = {
            vehicleIds,
            materialIds,
            taskIds,
            loadAreasIds,
            unloadAreasIds,
            gpsLoadAreasIds,
            gpsUnloadAreasIds,
            includeEmptyTrips: this.includeEmptyTrips,
            distanceFrom: this.distance.from,
            distanceTo: this.distance.to,
        };

        if (this.fromTimestamp) {
            opts.starttime = this.fromTimestamp.toString();
        }

        if (this.toTimestamp) {
            opts.endtime = this.toTimestamp.toString();
        }

        return opts;
    };
}
